import { useEffect } from "react";

import notificationSound from "assets/audio/notification-sound.mp3";

// A hidden tab's play() may be deferred by the browser until the tab is
// focused; past this window the arrival is stale and the pending play is
// aborted so it cannot chime on a much later tab switch.
export const ARRIVAL_SOUND_MAX_PENDING_MS = 5 * 60 * 1000;

/**
 * Plays the patient-arrived chime for the lifetime of its arrival banner.
 *
 * Every open tab attempts to play (so the chime still sounds when the browser
 * is minimized), but the sound is tied to the banner: dismissing the banner —
 * which the layout syncs across tabs (Intercom 215475543630993) — unmounts it
 * everywhere and cancels any play() a hidden tab's browser was holding until
 * focus, so an acknowledged arrival never chimes again on a tab switch.
 *
 * `eventKey` identifies the arrival event. A re-arrival replaces the banner
 * without remounting it (same alert id in one batched render), so a changed
 * eventKey is what re-runs the effect and chimes for the new event.
 */
export default function useArrivalNotificationSound(
  enabled: boolean,
  eventKey: number
) {
  useEffect(() => {
    if (!enabled) {
      return undefined;
    }
    const audio = new Audio(notificationSound);
    let settled = false;
    const markSettled = () => {
      settled = true;
    };
    // Autoplay rejections are expected (no interaction with this tab yet).
    audio.play().then(markSettled).catch(markSettled);

    const staleTimer = setTimeout(() => {
      if (!settled) {
        audio.pause();
      }
    }, ARRIVAL_SOUND_MAX_PENDING_MS);

    return () => {
      clearTimeout(staleTimer);
      if (!settled) {
        audio.pause();
      }
    };
  }, [enabled, eventKey]);
}
